import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { locationRoutes } from '@/lib/route-map';
import { writeActiveTenant } from '@/lib/active-tenant';
import { resolveApp, type DiscoveryResponse } from '@/lib/active-tenant-resolve';
import { acceptFailure, roleLabel, type AcceptFailure, type InvitePeek } from './invite-copy';

// =====================================================================
// PB-002 — invitation acceptance, public route at /invite/:token.
//
// The worker serves this SPA shell for a browser navigation to the link
// (routes/invite.ts `wantsDocument`); this screen then reads the public,
// non-consuming peek from the SAME URL as JSON. Acceptance is ONE explicit
// click → POST /invite/:token/accept with NO body: identity, role and scope
// are decided by the server from the session and the stored invitation.
// Signed out, the call is never made — the visitor goes through /login and
// comes back to this same link (redirect_after).
// =====================================================================

type Me = { email: string } | null;

/** Identity probe without lib/api's 401 → /login auto-redirect: being signed out is a normal state here. */
async function loadSignedInEmail(): Promise<Me> {
  try {
    const res = await fetch('/api/me', { credentials: 'same-origin', headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const body = (await res.json()) as { user?: { email?: unknown } };
    return typeof body.user?.email === 'string' ? { email: body.user.email } : null;
  } catch {
    return null;
  }
}

/** Where the new member lands: the accepted business only, through the same resolver /app uses. */
async function landingFor(businessId: string): Promise<string> {
  const discovery = await api.get<DiscoveryResponse>('/api/businesses');
  const only = { businesses: discovery.businesses.filter((b) => b.id === businessId) };
  const r = resolveApp(only, null);
  if (r.kind !== 'location') return '/app';
  writeActiveTenant({ businessId: r.businessId, locationId: r.locationId });
  return locationRoutes.overview(r.businessId, r.locationId);
}

export function InviteAccept() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [peek, setPeek] = useState<InvitePeek | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [me, setMe] = useState<Me | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<AcceptFailure | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ invitation: InvitePeek }>(`/invite/${encodeURIComponent(token)}`)
      .then((res) => !cancelled && setPeek(res.invitation))
      // only the server's generic 404 means "this link is dead"; a 5xx / network drop is temporary
      .catch((err: unknown) => !cancelled && ((err as { status?: number })?.status === 404 ? setInvalid(true) : setLoadFailed(true)));
    void loadSignedInEmail().then((m) => !cancelled && setMe(m));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    if (!peek || submitting) return;
    setSubmitting(true);
    setFailure(null);
    try {
      const res = await api.post<{ membership: { businessId: string } }>(`/invite/${encodeURIComponent(token)}/accept`);
      const target = await landingFor(res.membership.businessId).catch(() => '/app');
      navigate(target, { replace: true });
    } catch (err) {
      setFailure(acceptFailure(err, peek, me?.email ?? null));
      setSubmitting(false);
    }
  };

  if (invalid || failure?.kind === 'invalid') return <InvalidInvite />;
  if (loadFailed) {
    return (
      <Shell>
        <p role="alert" className="text-text-secondary">
          We couldn't load your invitation just now. Refresh this page in a moment to try again.
        </p>
      </Shell>
    );
  }
  if (!peek || me === undefined) {
    return (
      <Shell>
        <p className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your invitation…
        </p>
      </Shell>
    );
  }

  const signInHref = `/login?redirect_after=${encodeURIComponent(`/invite/${token}`)}`;
  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Team invitation</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Join {peek.businessName}</h1>
        <p className="text-base text-text-secondary">
          You've been invited to help manage {peek.businessName} on pageboss as {roleLabel(peek.role)}.
        </p>
      </header>

      <section className="flex flex-col gap-4 rounded-xl border border-border-subtle bg-card p-6 shadow-sm">
        <p className="flex items-start gap-2.5 text-sm text-text-secondary">
          <Users className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
          <span>
            This invitation is for <span className="font-medium text-foreground">{peek.emailDisplay}</span>. It expires on{' '}
            {new Date(peek.expiresAt).toLocaleDateString()}.
          </span>
        </p>

        {failure?.kind === 'message' ? (
          <div role="alert" className="flex flex-col gap-2 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger-fg">
            <p>{failure.text}</p>
            {failure.signOut ? (
              <form method="post" action="/auth/sign-out">
                <Button type="submit" variant="outline" size="sm">Sign out</Button>
              </form>
            ) : null}
            {failure.dashboard ? (
              <a href="/app" className="w-fit font-medium text-brand-700 hover:text-brand-800">Go to your dashboard</a>
            ) : null}
          </div>
        ) : null}

        {me ? (
          <div className="flex flex-col gap-2">
            <Button type="button" size="lg" className="w-fit px-5" onClick={accept} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? 'Accepting…' : 'Accept invitation'}
            </Button>
            <p className="text-xs text-text-tertiary">Signed in as {me.email}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <a
              href={signInHref}
              className={cn(buttonVariants({ size: 'lg' }), 'w-fit px-5')}
            >
              Sign in to accept
            </a>
            <p className="text-xs text-text-tertiary">Sign in with Google or Apple using {peek.emailDisplay}.</p>
          </div>
        )}
      </section>
    </Shell>
  );
}

function InvalidInvite() {
  return (
    <Shell>
      <h1 className="text-2xl font-semibold tracking-tight">This invitation isn't valid anymore</h1>
      <p className="text-text-secondary">
        It may have expired, been cancelled, or already been used. Ask the person who invited you to send a new link.
      </p>
      <a href="/app" className="w-fit text-sm font-medium text-brand-700 hover:text-brand-800">Go to your dashboard</a>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto flex max-w-2xl flex-col gap-8 px-6 py-12">
        <a href="/" className="inline-flex w-fit items-center text-sm font-semibold tracking-tight text-text-secondary hover:text-foreground">
          pageboss
        </a>
        {children}
      </div>
    </main>
  );
}
